/** LocalStorage key; bump the suffix to invalidate the schema. Browser-global (not per-payment). */
export const STORAGE_KEY = 'npp.experiments.v1';

/** Sticky window: once a page version is shown, keep showing it for 14 days. */
export const STICKY_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

/** experimentName -> variantCode; an empty object means "the user saw the unmodified page". */
export type SnapshotAssignments = Record<string, string>;

export interface ExperimentSnapshot {
  assignments: SnapshotAssignments;
  expiresAt: number; // epoch ms
}

function isValidSnapshot(value: unknown): value is ExperimentSnapshot {
  if (!value || typeof value !== 'object') return false;
  const s = value as Partial<ExperimentSnapshot>;
  if (!Number.isFinite(s.expiresAt)) return false;
  if (!s.assignments || typeof s.assignments !== 'object') return false;
  return true;
}

/**
 * The current non-expired snapshot, or undefined when absent / expired / corrupt.
 * Browser-global: a single key, independent of payment.
 */
export function readSnapshot(now: number = Date.now()): ExperimentSnapshot | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidSnapshot(parsed) || parsed.expiresAt <= now) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

/**
 * Persist the frozen snapshot with its expiry. No-op when `expiresAt` is not finite.
 * Never throws (storage unavailable/quota) so it cannot break the payment flow.
 */
export function writeSnapshot(assignments: SnapshotAssignments, expiresAt: number): void {
  if (!Number.isFinite(expiresAt)) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ assignments, expiresAt }));
  } catch {
    // storage unavailable/quota — never break the payment flow
  }
}

/** Remove the snapshot. Retained for tests / explicit reset; not called on payment finalisation. */
export function clear(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable — never break the payment flow
  }
}

/** `?abForce=name:variant` — non-prod only. Returns undefined in production or on a malformed value. */
export function resolveForce(
  searchParams: URLSearchParams,
  environment: string
): { name: string; variant: string } | undefined {
  if ((environment ?? '').toLowerCase() === 'production') return undefined;
  const raw = searchParams.get('abForce');
  if (!raw) return undefined;
  const [name, variant] = raw.split(':');
  if (!name || !variant) return undefined;
  return { name, variant };
}
